import { TransferRail } from '@varo/graphql-schema-types';
import {
  Stack,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from 'expo-router';
import { flowNameAttributes } from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useRefreshSystemBanners } from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import usePreventBackNavigation from 'shared/hooks/usePreventBackNavigation/usePreventBackNavigation';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';
import { formatCurrency } from 'shared/utils/string/currency';

const TRANSFER_SUCCESS_SCREEN_KEY = 'instantTransferSuccess';

function TransferSuccessScreen() {
  const { t } = useTranslation('feeBasedInstantTransfer');
  const router = useRouter();
  const navigation = useNavigation();
  const { doneButtonTitle } = useDefaultErrorStrings();
  const { rail, amount, transferSpeedMessage, flowName } =
    useLocalSearchParams<{
      rail?: string;
      amount?: string;
      transferSpeedMessage?: string;
      flowName?: string;
    }>();
  const flowAttributes = flowNameAttributes(flowName);
  const { refreshSystemBanners } = useRefreshSystemBanners();
  const isScheduled = rail === TransferRail.Ach;

  useEffect(() => {
    refreshSystemBanners();
  }, [refreshSystemBanners]);

  const isLeavingRef = useRef(false);
  usePreventBackNavigation(true, (action) => {
    if (isLeavingRef.current) {
      navigation.dispatch(action);
    }
  });

  const handleDone = () => {
    isLeavingRef.current = true;
    navigateBack();
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false }} />
      <SuccessScreen
        visual="PLANE_A_ANIMATED"
        screenAnalyticsEventKey={TRANSFER_SUCCESS_SCREEN_KEY}
        customAttributes={flowAttributes}
        eyebrowText={t('success.eyebrow')}
        titleText={t(
          isScheduled ? 'success.scheduledTitle' : 'success.transferredTitle',
          { amount: formatCurrency(amount) },
        )}
        content={
          transferSpeedMessage ? (
            <BodyBaseText numberOfLines={0} style={styles.message}>
              {transferSpeedMessage}
            </BodyBaseText>
          ) : null
        }
        primaryButton={{
          label: doneButtonTitle,
          onPress: handleDone,
          analyticsId: 'transferMoneySuccessDone',
          customAttributes: flowAttributes,
        }}
        secondaryButton={{
          label: t('success.seeTransfers'),
          analyticsId: 'transferMoneySuccessSeeTransfers',
          customAttributes: flowAttributes,
          onPress: () =>
            router.replace('/move-money/transfer/transfer-history'),
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  message: { textAlign: 'center' },
});

export default TransferSuccessScreen;
